import type { JsonObject, JsonValue } from "./json";

/**
 * The wire protocol between the app frame and the relay.
 *
 * ONE hop, as of #22 (`docs/premises.md`):
 *
 *   app frame  --WebSocket-->  relay (DO)
 *
 * ⚠️ There used to be two, with a broker frame in the middle and a whole
 * `postMessage` half of this file describing the first — `AppRegister`,
 * `BrokerLoaded`, `BrokerReady`, `BrokerToApp` and their parsers. All of it is
 * deleted. It survived commit 2 of #22 only because **its own tests kept it
 * alive**: test files are knip entry points, so an export consumed by nothing
 * but its spec reports as used. CLAUDE.md names that blind spot; this is what
 * it looks like in practice.
 *
 * ## Why this went to 2 at stage 7
 *
 * `welcome.install` became NULLABLE, because an unpaired socket lands on a
 * lobby object that has no install id to give it — it has a pairing code
 * instead. Every other stage-7 addition is additive and would have needed no
 * bump: `welcome.code`, and the `paired` / `unpaired` messages, which a peer
 * that predates them fails to parse and ignores.
 *
 * That one nullable field is the reason. A v1 broker meeting a v2 relay would
 * otherwise fail `welcome`'s parse and sit there announcing nothing, which is
 * the silent half of a skew. Bumping turns it into a refused `hello` with a
 * stated reason on the very first message — the same failure, said out loud.
 *
 * ## Why it went to 3 — the frame proves the user
 *
 * `Hello.install` became `Hello.token`: a field **RENAME**, not an addition.
 * That is the whole argument. A v2 broker meeting a v3 relay would send
 * `install` and the relay would see no token at all — not a malformed one, not
 * a refused one, but a socket that looks anonymous. There is no anonymous
 * socket any more, so that reading has nowhere safe to land, and the version
 * number is what turns it into a refused `hello` with a stated reason.
 *
 * `Paired` and `Unpaired` go with the mechanism they described, `namespace`
 * goes with the storage it keyed, and `welcome` now names a VERIFIED user
 * instead of handing back a credential.
 *
 * ## v4, 2026-09-15 — the origin stops being carried
 *
 * `Hello.brokerOrigin` is REMOVED and `appOrigin` is no longer read off a
 * `frames` payload. Both are now taken from the WebSocket handshake's `Origin`
 * header, which the browser stamps and page script cannot set.
 *
 * Another removal rather than an addition, and the same argument as v3: a v3
 * frame meeting a v4 relay would send an origin the relay ignores, and — worse
 * — a v4 frame meeting a v3 relay would send NO origin where one is required,
 * which the older relay reads as a malformed hello. The version number turns
 * both into a refusal with a stated reason instead of a socket that behaves
 * almost correctly.
 *
 * ## v5 — the project is the unit (#67)
 *
 * `activate { workspaceId }` is REPLACED by `front`, which carries nothing: the
 * relay takes the project from the sender's verified token, so there is no id
 * left for a frame to state. `SessionState`'s `no_active_workspace` becomes
 * `no_active_project`, and the session push names the active project and the
 * projects that have a control app instead of a workspace. A v4 peer would send
 * a message the relay no longer knows and read a state it cannot name, so this
 * is a refusal with a stated reason rather than a socket that half works.
 */
export const WIRE_PROTOCOL_VERSION = 5;

/**
 * The heartbeat (#66): fixed strings, not messages. The relay hands this pair to
 * `state.setWebSocketAutoResponse`, so the runtime answers a ping byte for byte
 * WITHOUT waking the Durable Object, and records when each socket last pinged.
 * Any other spelling of a ping reaches `webSocketMessage` and bills the object
 * awake on every beat. Part of v5 because v5 had not been deployed when it was
 * added.
 */
export const HEARTBEAT_PING = '{"t":"ping"}';
export const HEARTBEAT_PONG = '{"t":"pong"}';
/** A hidden tab runs chained timers at most once a minute in Chrome, so no shorter. */
export const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * `docs/error-taxonomy.md`'s ceiling. Not an opening bid: nothing in this system blocks on a human
 * (`docs/tool-surface.md`), so a call that has not answered in 30s has failed rather than waited.
 */
export const CALL_BUDGET_MS = 30_000;

/**
 * How deep a per-frame queue may get before calls are refused (`docs/error-taxonomy.md`: "a
 * queue-depth error rather than unbounded growth"). Refusal is not an error
 * code — it is `blocked`, which says retry is safe, because a call refused
 * BEFORE it started is confirmably not started.
 */
export const MAX_QUEUE_DEPTH = 4;

/**
 * The relay's three session states (`docs/tool-surface.md`), and they live HERE rather than in
 * `relay/sessions.ts` because they now cross the socket. The relay is the only
 * thing entitled to compute one; everything downstream reports what it is told.
 */
export type SessionState = "no_session" | "no_active_project" | "live";

/** A tool as it travels: the manifest's shape, minus anything only we need. */
export type WireTool = {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: JsonObject;
  readonly destructiveHint?: boolean;
  readonly canAwaitUser?: boolean;
  readonly settlesBeforeResolving?: boolean;
};

/**
 * What an app frame REPORTS about itself. Every field is its own claim.
 *
 * That is fine for all of them: an app lying about its project id can only
 * misroute calls to itself. What is deliberately absent is the origin — see
 * `FrameDescriptor`.
 */
export type FrameReport = {
  readonly frameId: string;
  readonly app: string;
  readonly workspaceId: string;
  readonly projectId: string;
  readonly projectUrl: string;
  readonly tools: readonly WireTool[];
};

/**
 * One connected app frame, as the RELAY sees it: what the frame reported, plus
 * the one field the frame does not get to choose.
 *
 * ⚠️ **`appOrigin` is attested, not reported.** It is the WebSocket handshake's
 * `Origin` header — stamped by the browser, unsettable by page script — and it
 * is the only signal of WHICH APP is talking, because the Giraffe token carries
 * no `app` claim.
 *
 * It used to be the broker's `event.origin` on the postMessage hop, which was
 * genuinely attested there and then **read straight back out of the socket
 * payload** at the relay, where anything could set it. The docs claimed the
 * strong property and the code had the weak one. v4 closes that.
 *
 * ⚠️ **Null is a real answer**: a non-browser client sends no `Origin`. Null
 * means "this socket attested nothing", never "some origin we could not read" —
 * a failure and an empty success must not share a shape.
 */
export type FrameDescriptor = FrameReport & {
  readonly appOrigin: string | null;
};

// ── app frame → relay ────────────────────────────────────────────────────────

export type Hello = {
  readonly t: "hello";
  readonly v: number;
  /**
   * The frame's token, forwarded verbatim. NEVER null: there is no anonymous
   * socket any more, so a hello without one is refused rather than landing
   * somewhere harmless.
   *
   * ⚠️ **It arrives HERE and never in the dial URL.** Cloudflare logs request
   * URLs on the public hop AND on the Worker → Durable Object hop (#17, already
   * measured); it does not log socket payloads. A browser `WebSocket` cannot
   * set a header, which rules out the obvious third option.
   */
  readonly token: string;
};

/**
 * What a frame reports about itself. Carries NO origin — see `FrameDescriptor`.
 */
export type FramesUpdate = {
  readonly t: "frames";
  readonly frames: readonly FrameReport[];
};

/**
 * The same message after the relay has attached the handshake origin.
 *
 * A separate type rather than a mutable field, so there is no moment inside the
 * relay where a frame's own claim about its origin exists at all. The parse
 * boundary is where untrusted input becomes a trusted shape, which is exactly
 * where attestation belongs.
 */
export type FramesAttested = {
  readonly t: "frames";
  readonly frames: readonly FrameDescriptor[];
};

/**
 * The control app's tab came to the front. Carries nothing on purpose: the
 * project it activates is the one the sender's verified token names, and only a
 * control-app connection is listened to (`relay/sessions.ts`).
 */
export type Front = {
  readonly t: "front";
};

export type CallResult = {
  readonly t: "result";
  readonly id: string;
  readonly result: JsonValue;
};

/** What an app frame sends. */
export type FrameMessage = Hello | FramesUpdate | Front | CallResult;

/** What the relay has after parsing one, origins attached. */
export type AttestedFrameMessage = Hello | FramesAttested | Front | CallResult;

// ── relay → app frame ────────────────────────────────────────────────────────

/** An id the token lists, with the name it gives it, if any (#37). Not an access grant. */
export type TokenListing = {
  readonly id: string;
  readonly name: string | null;
};

export type Welcome = {
  readonly t: "welcome";
  readonly v: number;
  /**
   * Who the relay decided this socket belongs to, having VERIFIED the token.
   * A derived fact, not the credential — the panel may display it.
   */
  readonly userId: string;
  /** `user_name` from the same verified token. Null when it carries none. */
  readonly userName: string | null;
  /**
   * The token's `workspaces[]` and `projects[]`, handed down WHOLE rather than
   * resolved here, because the frame reports its own workspace and project in
   * `frames` — after this message. The panel matches its reported ids against
   * them; it never decodes the token itself, so every name it prints is one the
   * relay verified.
   *
   * Additive, so no version bump: **null is "this relay predates #37"**, which
   * the panel renders as a bare id. An empty list is a different answer — the
   * token listed nothing — and the two must not share a shape.
   */
  readonly workspaces: readonly TokenListing[] | null;
  readonly projects: readonly TokenListing[] | null;
};

export type RelayCall = {
  readonly t: "call";
  readonly id: string;
  readonly frameId: string;
  readonly tool: string;
  readonly args: JsonObject;
  readonly budgetMs: number;
};

/** The relay stopped waiting. The frame should abandon the call if it can. */
export type Abandon = {
  readonly t: "abandon";
  readonly id: string;
  readonly reason: string;
};

/**
 * What the RELAY believes about the session, pushed down every app frame's own
 * socket whenever it changes — and the message that makes the panel honest
 * (#10).
 *
 * ## Why it has to be a push
 *
 * Activation is NOT a record of what this frame did. The active project moves
 * when ANOTHER tab's control app comes to the front, and it clears when its
 * project loses its last frame — neither caused by the frame being told. A
 * frame that only knew its own history could not be right about either, which
 * is precisely the defect #10 closed.
 *
 * ## Why it carries no tools
 *
 * ⚠️ This used to rest on the broker — it *"must never learn what a tool is"*,
 * and a catalogue in the envelope would have put a second copy of the tool
 * contract on the shared pairing origin. #22 deleted the broker and that
 * origin both, so the reason now rests on what it always protected: **the
 * envelope and the tool contract are versioned separately**, which is why
 * `wire/` may not import `manifest/` (`wire-reaches-only-safety`) and why a
 * tool change must not become a protocol change. The state and the active
 * project ids are enough: an app frame knows its OWN project id because it
 * registered with it, so "is the target me" and "does my project have a control
 * app" are comparisons it can make locally.
 *
 * ## Why it carries no `v`
 *
 * Deliberately additive, like `call` and `abandon`. A peer that predates this
 * message fails its parse and ignores it, which leaves it behaving exactly as
 * it did before; carrying a `v` would instead route it through `versionOk` and
 * couple it to a bump neither hop needs.
 */
export type SessionUpdate = {
  readonly t: "session";
  readonly state: SessionState;
  readonly activeProjectId: string | null;
  /** Projects with a verified control-app frame connected, sorted. */
  readonly controlProjectIds: readonly string[];
};

export type RelayMessage = Welcome | RelayCall | Abandon | SessionUpdate;

// ── app frame → relay, over plain HTTPS ─────────────────────────────────────

/**
 * Why the socket went, as far as this frame can tell.
 *
 * ⚠️ These three used to render identically — `disconnected` and an endless
 * backoff — so a relay refusing the token and a network that never let the
 * socket through looked the same to the person reading the panel, who is the
 * only one who can see it. A failure and an empty success sharing a shape, one
 * level up.
 *
 * - `refused`: the relay closed it with `4401` and said why (`refuse` in
 *   `relay/durableObject.ts`). The reason is the relay's own string.
 * - `unreachable`: it closed without ever opening. The browser hides the
 *   status of a failed upgrade, so this cannot say WHAT stopped it — only that
 *   nothing on our side was reached, `attempts` dials in a row. A Worker that
 *   answered the upgrade with an HTTP error lands here too; the ones it has
 *   (`missing ref`, `malformed ref`) are unreachable from this module, which
 *   mints the ref.
 * - `dropped`: it opened and then went, for a reason other than a refusal.
 *   `code` is the close code, or null when the event carried none.
 */
export type SocketDrop =
  | { readonly kind: "refused"; readonly reason: string }
  | { readonly kind: "unreachable"; readonly attempts: number }
  | { readonly kind: "dropped"; readonly code: number | null };

/**
 * A drop, reported by the frame to `POST /report` rather than over the socket.
 *
 * ⚠️ **It exists because of `unreachable`.** A socket that never opened never
 * reached Cloudflare, so nothing the relay logs about sockets can record it. A
 * plain HTTPS request is what a middlebox that breaks WebSocket upgrades still
 * lets through — the panel itself loaded over one — so this is the only way
 * that failure is ever seen from our side.
 *
 * Carries the REF and never the token: a report is unauthenticated, and the
 * ref is a public hash that `relay.socket_admitted` also logs, which is what
 * correlates the two. `consecutive` is how many drops in a row this frame has
 * had since it was last welcomed.
 */
export type DropReport = {
  readonly t: "drop_report";
  readonly v: number;
  readonly ref: string;
  readonly consecutive: number;
  readonly drop: SocketDrop;
};
